import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Phone, Calendar } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    yourName: '',
    yourEmail: '',
    businessName: '',
    contactInfo: '',
    isAware: false,
    notes: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/contact`, {
        name: formData.yourName,
        email: formData.yourEmail,
        phone: formData.contactInfo,
        company: formData.businessName,
        message: `REFERRAL: ${formData.businessName}\nContact: ${formData.contactInfo}\nAware of referral: ${formData.isAware ? 'Yes' : 'No'}\nNotes: ${formData.notes}`,
        is_referral: true
      });

      const submittedName = formData.yourName;
      
      setFormData({
        yourName: '',
        yourEmail: '',
        businessName: '',
        contactInfo: '',
        isAware: false,
        notes: ''
      });

      // Navigate to thank you page
      navigate('/thank-you', { 
        state: { 
          isReferral: true, 
          name: submittedName 
        } 
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Refer a Business, Earn <span className="text-cyan-500">$300</span>
          </h1>
          <p className="text-xl text-gray-600">
            Know a business that needs IT support? Send them our way and get rewarded.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">How It Works</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Submit a Referral</h3>
              <p className="text-gray-600">Fill out the form below with the business contact information</p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">We Reach Out</h3>
              <p className="text-gray-600">We'll contact them and provide a consultation</p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">You Get Paid</h3>
              <p className="text-gray-600">Receive $300 after their first month of service</p>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Form Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Submit a Referral</h2>
            <p className="text-gray-600 mb-8">Tell us about the business you'd like to refer</p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Your Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="yourName"
                      value={formData.yourName}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      data-testid="referral-your-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="yourEmail"
                      type="email"
                      value={formData.yourEmail}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      data-testid="referral-your-email"
                    />
                  </div>
                </div>
              </div>

              {/* Referral Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Referral Information</h3>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business/Contact Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      required
                      placeholder="ABC Company"
                      data-testid="referral-business-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email or Phone <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="contactInfo"
                      value={formData.contactInfo}
                      onChange={handleChange}
                      required
                      placeholder="contact@company.com or 555-123-4567"
                      data-testid="referral-contact-info"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2 mb-4">
                  <Checkbox
                    id="isAware"
                    checked={formData.isAware}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isAware: checked }))}
                    data-testid="referral-aware-checkbox"
                  />
                  <label htmlFor="isAware" className="text-sm text-gray-700">
                    They are aware I'm referring them to Microsystems Networking
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Notes (optional)
                  </label>
                  <Textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Any additional information about the referral..."
                    rows={4}
                    data-testid="referral-notes"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                disabled={isSubmitting}
                data-testid="referral-submit-button"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Referral'}
              </Button>
            </form>
          </div>

          {/* Terms Section */}
          <div className="mt-8 bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Referral Program Terms</h3>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                Referral reward of $300 is paid after the referred business completes their first month of service
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                Limit one reward per referred company
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                Microsystems Networking may decline referrals that are not a fit for our services
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                Referral must be a new client (not a current or previous client)
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                Payment will be made via check or electronic transfer
              </li>
            </ul>
          </div>

          {/* CTA Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline" 
              size="lg"
              className="border-gray-300"
              onClick={() => window.location.href = 'tel:+17746447101'}
              data-testid="referral-call-button"
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Now
            </Button>
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => window.location.href = '/contact'}
              data-testid="referral-book-consult-button"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Book a Free Consult
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReferralsPage;
